import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const escapeHtml = (value) =>
	String(value || "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");

/** Convert inline markdown (**bold**, *italic*, `code`) to safe HTML. */
const inlineToHtml = (text) => {
	let html = escapeHtml(text);
	html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
	html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
	html = html.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
	return html;
};

const InlineHtml = ({ text, sx }) => (
	<Box
		component="span"
		sx={{
			"& strong": { fontWeight: 700 },
			"& em": { fontStyle: "italic" },
			"& code": {
				fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
				fontSize: "0.85em",
				px: 0.4,
				py: 0.1,
				borderRadius: 0.5,
				bgcolor: "rgba(0,0,0,0.06)",
			},
			...sx,
		}}
		dangerouslySetInnerHTML={{ __html: inlineToHtml(text) }}
	/>
);

const isBullet = (line) => /^\s*([-*•]|\d+\.)\s+/.test(line);
const bulletText = (line) => line.replace(/^\s*([-*•]|\d+\.)\s+/, "");
const isHeading = (line) => /^#{1,3}\s+/.test(line) || /^\*\*[^*].*:\*\*\s*$/.test(line.trim());
const headingText = (line) => {
	const trimmed = line.trim();
	if (/^#{1,3}\s+/.test(trimmed)) return trimmed.replace(/^#{1,3}\s+/, "");
	return trimmed.replace(/^\*\*/, "").replace(/\*\*$/, "");
};

/**
 * Lightweight markdown renderer for chat bubbles (bold, italics, lists, headings).
 * Avoids adding a heavy dependency for Relook assistant replies.
 */
const ChatMarkdown = ({ content, color = "inherit" }) => {
	const lines = String(content || "").replace(/\r\n/g, "\n").split("\n");
	const blocks = [];
	let i = 0;

	while (i < lines.length) {
		const line = lines[i];

		if (!line.trim()) {
			i += 1;
			continue;
		}

		if (isBullet(line)) {
			const items = [];
			while (i < lines.length && isBullet(lines[i])) {
				items.push(bulletText(lines[i]));
				i += 1;
			}
			blocks.push({ type: "list", items });
			continue;
		}

		if (isHeading(line)) {
			blocks.push({ type: "heading", text: headingText(line) });
			i += 1;
			continue;
		}

		const para = [];
		while (i < lines.length && lines[i].trim() && !isBullet(lines[i]) && !isHeading(lines[i])) {
			para.push(lines[i]);
			i += 1;
		}
		blocks.push({ type: "paragraph", text: para.join(" ") });
	}

	if (!blocks.length) {
		return (
			<Typography variant="body2" sx={{ lineHeight: 1.55, color, whiteSpace: "pre-wrap" }}>
				{content}
			</Typography>
		);
	}

	return (
		<Box sx={{ color, "& > *:last-child": { mb: 0 } }}>
			{blocks.map((block, index) => {
				if (block.type === "heading") {
					return (
						<Typography
							key={index}
							variant="subtitle2"
							sx={{ fontWeight: 700, mt: index ? 1.25 : 0, mb: 0.5, lineHeight: 1.4 }}>
							<InlineHtml text={block.text} />
						</Typography>
					);
				}

				if (block.type === "list") {
					return (
						<Box
							key={index}
							component="ul"
							sx={{
								m: 0,
								mt: 0.25,
								mb: 1,
								pl: 2.25,
								"& li": { mb: 0.35 },
							}}>
							{block.items.map((item, itemIndex) => (
								<Typography key={itemIndex} component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
									<InlineHtml text={item} />
								</Typography>
							))}
						</Box>
					);
				}

				return (
					<Typography key={index} variant="body2" sx={{ lineHeight: 1.55, mb: 1 }}>
						<InlineHtml text={block.text} />
					</Typography>
				);
			})}
		</Box>
	);
};

export default ChatMarkdown;
